import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useFetch } from '../../../hooks/useFecth';

function defaultRange() {
    const to = new Date();
    const from = new Date(to.getTime() - 7 * 86400000);
    const ymd = date => date.toISOString().slice(0, 10);
    return { from: ymd(from), to: ymd(to) };
}

function money(value, currency = 'BRL') {
    return Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: currency || 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function formatDateTime(value) {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function saleStatus(status) {
    const key = String(status || '').toLowerCase();
    if (['approved', 'validated', 'paid'].includes(key)) return { label: 'Aprovada', color: 'success' };
    if (['declined', 'deleted', 'rejected', 'cancelled', 'canceled'].includes(key)) {
        return { label: 'Recusada', color: 'error' };
    }
    if (['pending', 'pending approval'].includes(key)) return { label: 'Pendente', color: 'warning' };
    return { label: status || 'Sem status', color: 'default' };
}

export default function BrokerSalesDialog({ open, onClose, broker }) {
    const isAwin = broker === 'awin';
    const initial = useMemo(defaultRange, []);
    const [from, setFrom] = useState(initial.from);
    const [to, setTo] = useState(initial.to);
    const [applied, setApplied] = useState(initial);
    const [page, setPage] = useState(1);

    const params = useMemo(
        () => ({
            from: applied.from,
            to: applied.to,
            ...(!isAwin ? { page, limit: 20 } : {}),
        }),
        [applied.from, applied.to, isAwin, page],
    );

    const { data, isFetching, isValidating, mutate } = useFetch(
        open ? `brokers/${broker}/sales` : null,
        { params },
    );

    const rows = data?.rows ?? [];
    const pages = data?.pages || 1;
    const title = isAwin ? 'Vendas Awin' : 'Vendas Lomadee';

    const applyRange = () => {
        setPage(1);
        setApplied({ from, to });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                {title}
                <Typography variant="body2" color="text.secondary" fontWeight={400}>
                    O que a loja parceira registrou no período. Se o código do app voltar, mostramos o resgate daqui.
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                    <TextField
                        size="small"
                        label="De"
                        type="date"
                        value={from}
                        onChange={e => setFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                    <TextField
                        size="small"
                        label="Até"
                        type="date"
                        value={to}
                        onChange={e => setTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                    <Button variant="contained" onClick={applyRange} sx={{ minWidth: 120 }}>
                        Buscar
                    </Button>
                </Stack>

                {isFetching || isValidating ? (
                    <Stack alignItems="center" py={4}>
                        <CircularProgress size={28} />
                    </Stack>
                ) : rows.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={3}>
                        Nenhuma venda neste período.
                    </Typography>
                ) : (
                    <Stack spacing={1.25} divider={<Divider flexItem />}>
                        {rows.map(row => {
                            const status = saleStatus(row.status);
                            return (
                                <Box key={`${row.id}-${row.code || ''}`}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography fontWeight={700} noWrap>
                                                {row.advertiserName || 'Loja sem nome'}
                                            </Typography>
                                            <Typography variant="body2">
                                                Compra {money(row.saleValue, row.currency)} · Comissão {money(row.commission, row.currency)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {formatDateTime(row.date)} · Pedido {row.id || '—'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {row.code
                                                    ? row.rescue
                                                        ? `Resgate #${row.rescue.id} · ${row.rescue.personName || 'sem nome'}${row.rescue.clubName ? ` · ${row.rescue.clubName}` : ''}`
                                                        : 'Tem código do app, mas ainda sem resgate nosso'
                                                    : 'Sem código do app'}
                                            </Typography>
                                        </Box>
                                        <Chip size="small" label={status.label} color={status.color} variant="outlined" />
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                )}

                {!isAwin && pages > 1 ? (
                    <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 2 }}>
                        <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                            Anterior
                        </Button>
                        <Typography variant="body2" alignSelf="center">
                            {page} / {pages}
                        </Typography>
                        <Button size="small" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                            Próxima
                        </Button>
                    </Stack>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => mutate()}>Atualizar</Button>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}
